import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { COUNTRIES, countryInfo, GENDERS, SKILL_SUGGESTIONS } from "../../constants/options";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const KEYS = ["display_name", "town", "region", "postcode_area", "country", "gender", "playing_age_min", "playing_age_max", "height_cm", "bio", "skills", "contact_mode", "public_email", "public_phone"];
const NUMS = ["playing_age_min", "playing_age_max", "height_cm"];

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="label-xs">{label}</label>
    <div className="mt-2">{children}</div>
  </div>
);

const SkillsInput = ({ skills, onChange }) => {
  const [draft, setDraft] = useState("");
  const add = (s) => {
    const v = s.trim();
    if (v && !skills.includes(v)) onChange([...skills, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="profile-skills-list">
        {skills.map((s) => (
          <span key={s} className="border border-primary/50 text-primary px-3 py-1 text-sm inline-flex items-center gap-2">
            {s}
            <button type="button" onClick={() => onChange(skills.filter((x) => x !== s))} aria-label={`Remove ${s}`} data-testid={`remove-skill-${s}`}><X size={12} /></button>
          </span>
        ))}
      </div>
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }} placeholder="Type a skill and press Enter" data-testid="profile-skill-input" />
      <div className="flex flex-wrap gap-2 mt-3">
        {SKILL_SUGGESTIONS.filter((s) => !skills.includes(s)).map((s) => (
          <button type="button" key={s} onClick={() => add(s)} className="border border-white/15 px-3 py-1 text-xs text-white/60 hover:border-primary hover:text-primary transition-colors" data-testid={`suggest-skill-${s}`}>+ {s}</button>
        ))}
      </div>
    </div>
  );
};

const ContactChoice = ({ form, set }) => (
  <div className="border border-white/10 p-6" data-testid="profile-contact-choice">
    <p className="label-xs">How should casting people contact you?</p>
    <RadioGroup value={form.contact_mode} onValueChange={(v) => set("contact_mode", v)} className="mt-4 space-y-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <RadioGroupItem value="form" className="mt-1" data-testid="contact-mode-form" />
        <span><span className="font-semibold">Contact form only</span><span className="block text-sm text-white/50">They send a message to your private inbox here. Your details stay hidden.</span></span>
      </label>
      <label className="flex items-start gap-3 cursor-pointer">
        <RadioGroupItem value="public" className="mt-1" data-testid="contact-mode-public" />
        <span><span className="font-semibold">Show my email and phone publicly</span><span className="block text-sm text-white/50">Anyone viewing your profile can see these details.</span></span>
      </label>
    </RadioGroup>
    {form.contact_mode === "public" && (
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <Input type="email" placeholder="Public email" value={form.public_email} onChange={(e) => set("public_email", e.target.value)} data-testid="profile-public-email-input" />
        <Input placeholder="Public phone" value={form.public_phone} onChange={(e) => set("public_phone", e.target.value)} data-testid="profile-public-phone-input" />
      </div>
    )}
  </div>
);

export const ProfileForm = ({ profile, onSaved }) => {
  const [form, setForm] = useState(() => Object.fromEntries(KEYS.map((k) => [k, profile[k] ?? (NUMS.includes(k) ? "" : "")])));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = (k) => ({ value: form[k], onChange: (e) => set(k, e.target.value) });
  const info = countryInfo(form.country);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form };
    NUMS.forEach((k) => { body[k] = body[k] === "" || body[k] === null ? null : Number(body[k]); });
    try {
      const { data } = await api.put("/profile/me", body);
      onSaved(data);
      setForm((f) => ({ ...f, postcode_area: data.postcode_area, country: data.country }));
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-8" data-testid="profile-form">
      <div className="grid md:grid-cols-3 gap-6">
        <Field label="Display name" className="md:col-span-3"><Input required {...inp("display_name")} data-testid="profile-name-input" /></Field>
        <Field label="Country">
          <Select value={form.country || "United Kingdom"} onValueChange={(v) => setForm((f) => ({ ...f, country: v, region: "" }))}>
            <SelectTrigger data-testid="profile-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Town / city"><Input {...inp("town")} placeholder="e.g. Manchester, Atlanta, Galway" data-testid="profile-town-input" /></Field>
        <Field label={info.regionLabel}>
          <Select key={info.name} value={form.region || undefined} onValueChange={(v) => set("region", v)}>
            <SelectTrigger data-testid="profile-region-select"><SelectValue placeholder={`Select ${info.regionLabel.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>{info.regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={info.postcodeLabel}><Input {...inp("postcode_area")} placeholder={info.postcodeHint} maxLength={10} data-testid="profile-postcode-input" /></Field>
        <div className="md:col-span-2 flex items-end">
          <p className={`text-sm ${profile.geo_found ? "text-primary" : "text-white/50"}`} data-testid="profile-geo-status">
            {profile.geo_found
              ? "Location found — you'll appear in distance searches."
              : "Add your postcode / ZIP or town and save, so you appear in 'within X miles' searches."}
          </p>
        </div>
        <Field label="Gender">
          <Select value={form.gender || undefined} onValueChange={(v) => set("gender", v)}>
            <SelectTrigger data-testid="profile-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Playing age (from – to)">
          <div className="flex gap-2">
            <Input type="number" min="0" max="110" {...inp("playing_age_min")} placeholder="25" data-testid="profile-age-min-input" />
            <Input type="number" min="0" max="110" {...inp("playing_age_max")} placeholder="35" data-testid="profile-age-max-input" />
          </div>
        </Field>
        <Field label="Height (cm)"><Input type="number" min="50" max="250" {...inp("height_cm")} placeholder="175" data-testid="profile-height-input" /></Field>
      </div>
      <Field label="About you"><Textarea rows={6} {...inp("bio")} placeholder="Experience, look, availability, anything casting should know…" data-testid="profile-bio-input" /></Field>
      <Field label="Skills & attributes"><SkillsInput skills={form.skills} onChange={(v) => set("skills", v)} /></Field>
      <ContactChoice form={form} set={set} />
      <button disabled={saving} className="bg-primary text-primary-foreground px-8 py-4 font-bold uppercase text-sm tracking-wider hover:bg-[hsl(43,100%,60%)] transition-colors disabled:opacity-60" data-testid="profile-save-button">
        {saving ? "Saving…" : "Save profile"}
      </button>
    </form>
  );
};
